import type { ColumnDef } from '@tanstack/react-table'
import {
	useEffect,
	useMemo,
	useRef,
	useState,
	type ChangeEvent,
	type KeyboardEvent,
} from 'react'
import type { RouterInputs, RouterOutputs } from 'shared/api'
import { trpcClient } from 'shared/api'
import { DataTable } from 'shared/components/data-table/data-table'
import { PageFrame } from 'shared/components/page-frame'
import { SectionTitle, Stat, Stats } from 'shared/components/section'
import { TONE_CLASS } from 'shared/components/tone-banner'
import { Badge } from 'shared/components/ui/badge'
import { Button } from 'shared/components/ui/button'
import { Input } from 'shared/components/ui/input'
import { Label } from 'shared/components/ui/label'
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from 'shared/components/ui/select'
import { useDataTable } from 'shared/hooks/use-data-table'
import { useTranslate } from 'shared/lib/i18n'
import { cn, readAsBase64 } from 'shared/lib/utils'

/**
 * A partner's file uploaded to the hub (Release 2, spec §11; ADR-0023): one file
 * is one batch and one Manifest addressed to the hub, and each row names its
 * Point. The admin picks whose file it is, reads the preview — per Point, what is
 * new, moved in, re-addressed, cancelled, and every refused row with its reason —
 * confirms the batch number the file suggested, and only that confirmation sends
 * the Manifest. The partner's adapter in the backend reads and plans the file;
 * this screen only sends it and renders the answer.
 *
 * The hub's look: the file in one row, then the preview as sections with a
 * title and a count — the Points, the refused rows, what a new revision changes —
 * each a table, and the one main act, the confirmed number and «Отправить на хаб»,
 * at the end.
 */

type Format = RouterInputs['uploads']['HubPreview']['format']
const FORMATS: Format[] = ['express139', 'teztez']

export type HubPreviewInput = RouterInputs['uploads']['HubPreview']
export type HubConfirmInput = RouterInputs['uploads']['HubConfirm']
export type HubPlan = RouterOutputs['uploads']['HubPreview']
export type HubApplied = RouterOutputs['uploads']['HubConfirm']

export interface HubUploadProps {
	/** Test seam: plans the file, writes nothing. Defaults to the real tRPC client. */
	preview?: (input: HubPreviewInput) => Promise<HubPlan>
	/** Test seam: sends the previewed file to the hub. Defaults to the real tRPC client. */
	confirm?: (input: HubConfirmInput) => Promise<HubApplied>
}

const previewViaTrpc = (input: HubPreviewInput) =>
	trpcClient.uploads.HubPreview.mutate(input)
const confirmViaTrpc = (input: HubConfirmInput) =>
	trpcClient.uploads.HubConfirm.mutate(input)

/** The tRPC error code of a failed call, when it carries one. */
function errorCode(err: unknown): string | undefined {
	const data = (err as { data?: { code?: unknown } } | null)?.data
	return typeof data?.code === 'string' ? data.code : undefined
}

function errorMessage(err: unknown): string {
	return err instanceof Error ? err.message : String(err)
}

/** An amount with its thousands grouped; the currency is said once, by the column or the line. */
function amount(value: number): string {
	return value.toLocaleString('ru-RU')
}

/** Whether confirming the plan would change anything at the hub. */
function hasWork(plan: HubPlan): boolean {
	return (
		plan.accepted > 0 ||
		plan.readdresses.length > 0 ||
		plan.cancels.length > 0 ||
		plan.updates.length > 0
	)
}

/**
 * Enter acts as Tab, as a scanner or scale typing into the field ends with
 * Enter: it moves the cursor to `next` instead of submitting anything.
 */
function enterMovesTo(next: () => HTMLElement | null) {
	return (event: KeyboardEvent<HTMLElement>) => {
		if (event.key !== 'Enter') return
		event.preventDefault()
		next()?.focus()
	}
}

/** Every table here comes whole from one answer: no server paging, sorting or filter. */
const WHOLE = {
	manualPagination: false,
	manualSorting: false,
	manualFiltering: false,
	enableRowSelection: false,
	initialState: { pagination: { pageIndex: 0, pageSize: 10000 } },
} as const

export function HubUpload({
	preview = previewViaTrpc,
	confirm = confirmViaTrpc,
}: HubUploadProps) {
	const { t } = useTranslate()
	const [format, setFormat] = useState<Format>('express139')
	const [fileBase64, setFileBase64] = useState<string | null>(null)
	const [fileName, setFileName] = useState('')
	const [dispatchRef, setDispatchRef] = useState('')
	const [plan, setPlan] = useState<HubPlan | null>(null)
	/** The batch number the admin confirms; starts as the one the preview used. */
	const [confirmedRef, setConfirmedRef] = useState('')
	/** The plan no longer stands (the backend said so): only a new preview helps. */
	const [stale, setStale] = useState(false)
	const [applied, setApplied] = useState<HubApplied | null>(null)
	const [error, setError] = useState<string | null>(null)
	const [busy, setBusy] = useState<'preview' | 'confirm' | null>(null)

	const previewButton = useRef<HTMLButtonElement>(null)
	const confirmButton = useRef<HTMLButtonElement>(null)
	const confirmRefInput = useRef<HTMLInputElement>(null)

	// The batch number is the one thing the admin confirms: after a preview the
	// cursor waits there, and Enter takes it on to the confirm button. The page
	// stays at the preview's top, so the admin reads it before reaching the field.
	useEffect(() => {
		if (plan) confirmRefInput.current?.focus({ preventScroll: true })
	}, [plan])

	const reset = () => {
		setPlan(null)
		setApplied(null)
		setError(null)
		setStale(false)
	}

	const handleFile = async (event: ChangeEvent<HTMLInputElement>) => {
		const file = event.target.files?.[0]
		reset()
		setDispatchRef('')
		if (!file) {
			setFileBase64(null)
			setFileName('')
			return
		}
		setFileName(file.name)
		setFileBase64(await readAsBase64(file))
	}

	const runPreview = async (ref: string) => {
		if (!fileBase64) return
		setBusy('preview')
		setError(null)
		setApplied(null)
		try {
			const next = await preview({
				format,
				fileBase64,
				fileName: fileName || undefined,
				dispatchRef: ref.trim() || undefined,
			})
			setPlan(next)
			setStale(false)
			setDispatchRef(next.dispatchRef)
			setConfirmedRef(next.dispatchRef)
		} catch (err) {
			setPlan(null)
			setError(errorMessage(err))
		} finally {
			setBusy(null)
		}
	}

	const confirmedMatches = plan !== null && confirmedRef.trim() === plan.dispatchRef

	const runConfirm = async () => {
		if (!fileBase64 || !plan || !confirmedMatches || stale) return
		setBusy('confirm')
		setError(null)
		try {
			const result = await confirm({
				format,
				fileBase64,
				fileName: fileName || undefined,
				dispatchRef: plan.dispatchRef,
				planHash: plan.planHash,
			})
			setApplied(result)
			setPlan(null)
		} catch (err) {
			// The plan moved since the preview: confirming it again cannot succeed.
			if (errorCode(err) === 'CONFLICT') setStale(true)
			setError(errorMessage(err))
		} finally {
			setBusy(null)
		}
	}

	const errorLine = error && (
		<p role='alert' className='text-sm text-destructive' data-testid='hub-upload-error'>
			{error}
		</p>
	)

	return (
		<PageFrame title='nav.hubUpload'>
			<div className='flex flex-col gap-2'>
				<form
					className='flex flex-wrap items-end gap-3'
					onSubmit={(event) => {
						event.preventDefault()
						void runPreview(dispatchRef)
					}}
				>
					<div className='flex flex-col gap-1.5'>
						<Label htmlFor='hub-upload-format'>{t('hubUpload.partnerLabel')}</Label>
						<Select
							value={format}
							onValueChange={(value) => {
								setFormat(value as Format)
								reset()
								setDispatchRef('')
							}}
						>
							<SelectTrigger
								id='hub-upload-format'
								className='w-44'
								data-testid='hub-upload-format'
							>
								<SelectValue />
							</SelectTrigger>
							<SelectContent>
								{FORMATS.map((f) => (
									<SelectItem key={f} value={f}>
										{t(`hubUpload.partner_${f}`)}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					</div>
					<div className='flex min-w-64 flex-1 flex-col gap-1.5'>
						<Label htmlFor='hub-upload-file'>{t('hubUpload.fileLabel')}</Label>
						<Input
							id='hub-upload-file'
							type='file'
							accept='.xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
							onChange={handleFile}
							data-testid='hub-upload-file'
						/>
					</div>
					<div className='flex flex-col gap-1.5'>
						<Label htmlFor='hub-upload-dispatch'>{t('hubUpload.dispatchLabel')}</Label>
						<Input
							id='hub-upload-dispatch'
							className='w-56 font-mono'
							value={dispatchRef}
							onChange={(event) => {
								setDispatchRef(event.target.value.toUpperCase())
								if (plan) reset()
							}}
							onKeyDown={enterMovesTo(() => previewButton.current)}
							placeholder={t('hubUpload.dispatchPlaceholder')}
							data-testid='hub-upload-dispatch'
						/>
					</div>
					<Button
						ref={previewButton}
						type='submit'
						disabled={!fileBase64 || busy !== null}
						data-testid='hub-upload-preview'
					>
						{busy === 'preview' ? t('hubUpload.checking') : t('hubUpload.preview')}
					</Button>
				</form>
				{!plan && errorLine}
			</div>

			{applied && <AppliedLine applied={applied} />}

			{plan && (
				<div className='flex min-w-0 flex-col gap-8' data-testid='hub-upload-plan'>
					<PlanView plan={plan} />

					<div className='flex flex-col gap-2'>
						{errorLine && <div className='self-end'>{errorLine}</div>}
						<div
							className='flex flex-wrap items-end justify-end gap-3'
							data-testid='hub-upload-confirm-bar'
						>
							{!hasWork(plan) && (
								<p
									className='mr-auto self-center text-sm text-muted-foreground'
									data-testid='hub-upload-nothing'
								>
									{t('hubUpload.nothingToSend')}
								</p>
							)}
							<div className='flex flex-col gap-1.5'>
								<Label htmlFor='hub-upload-confirm-ref'>
									{t('hubUpload.confirmRefLabel')}
								</Label>
								<Input
									id='hub-upload-confirm-ref'
									ref={confirmRefInput}
									className='w-56 font-mono'
									value={confirmedRef}
									onChange={(event) => setConfirmedRef(event.target.value.toUpperCase())}
									onKeyDown={enterMovesTo(() => confirmButton.current)}
									data-testid='hub-upload-confirm-ref'
								/>
							</div>
							{confirmedMatches && !stale ? (
								<Button
									ref={confirmButton}
									type='button'
									onClick={() => void runConfirm()}
									disabled={!hasWork(plan) || busy !== null}
									data-testid='hub-upload-confirm'
								>
									{busy === 'confirm' ? t('hubUpload.confirming') : t('hubUpload.confirm')}
								</Button>
							) : (
								// Another batch number (or a plan that moved) needs its own preview.
								<Button
									ref={confirmButton}
									type='button'
									variant='outline'
									onClick={() => void runPreview(confirmedRef)}
									disabled={!confirmedRef.trim() || busy !== null}
									data-testid='hub-upload-repreview'
								>
									{t('hubUpload.repreview')}
								</Button>
							)}
						</div>
					</div>
				</div>
			)}
		</PageFrame>
	)
}

function PlanView({ plan }: { plan: HubPlan }) {
	const { t } = useTranslate()
	const amountTotal = plan.points.reduce((sum, p) => sum + p.amountTotal, 0)
	const changes =
		plan.readdresses.length + plan.cancels.length + plan.updates.length + plan.conflicts.length

	return (
		<>
			<section className='flex flex-col gap-2'>
				<SectionTitle
					title={
						<>
							{t('hubUpload.planTitle')}{' '}
							<span className='font-mono' data-testid='hub-upload-plan-ref'>
								{plan.dispatchRef}
							</span>{' '}
							<span
								className='ml-1 font-normal text-muted-foreground'
								data-testid='hub-upload-revision'
							>
								{plan.previousRevision === null
									? t('hubUpload.revisionNew', { revision: String(plan.revision) })
									: t('hubUpload.revisionNext', {
											from: String(plan.previousRevision),
											revision: String(plan.revision),
										})}
							</span>
						</>
					}
					hint={t('hubUpload.planHint')}
				/>
				<Stats testId='hub-upload-summary'>
					<Stat label={t('hubUpload.rows')} value={plan.rows} />
					<Stat label={t('hubUpload.accepted')} value={plan.accepted} testId='hub-upload-accepted' />
					<Stat
						label={t('hubUpload.refusedCount')}
						value={plan.refused.length}
						testId='hub-upload-refused-count'
						tone='bad'
					/>
					<Stat label={t('hubUpload.amountTotal')} value={amount(amountTotal)} />
					{plan.boxes > 0 && (
						<>
							<Stat label={t('hubUpload.boxes')} value={plan.boxes} testId='hub-upload-boxes' />
							<Stat label={t('hubUpload.mixBoxes')} value={plan.mixBoxes} testId='hub-upload-mix-boxes' />
						</>
					)}
				</Stats>
				<PointsTable plan={plan} />
			</section>

			<section className='flex flex-col gap-2'>
				<SectionTitle title={t('hubUpload.refusedTitle')} count={plan.refused.length} />
				<RefusedTable plan={plan} />
			</section>

			{(plan.previousRevision !== null || changes > 0) && (
				<section className='flex flex-col gap-2'>
					<SectionTitle title={t('hubUpload.changesTitle')} count={changes} />
					<ChangesTable plan={plan} />
				</section>
			)}
		</>
	)
}

type PlanPoint = HubPlan['points'][number]

function PointsTable({ plan }: { plan: HubPlan }) {
	const { t } = useTranslate()
	const movedIn = plan.points.some((p) => p.movedIn > 0)
	const boxes = plan.boxes > 0
	const columns = useMemo<ColumnDef<PlanPoint>[]>(() => {
		const count = (
			id: string,
			header: string,
			pick: (p: PlanPoint) => number,
		): ColumnDef<PlanPoint> => ({
			id,
			header,
			meta: { headClassName: 'text-right', cellClassName: 'text-right tabular-nums' },
			cell: ({ row }) => (
				<span className={cn(pick(row.original) === 0 && 'text-muted-foreground')}>
					{pick(row.original)}
				</span>
			),
		})
		return [
			{
				id: 'code',
				header: t('hubUpload.colCode'),
				cell: ({ row }) => <span className='font-mono'>{row.original.pointCode}</span>,
			},
			{ id: 'point', header: t('hubUpload.colPoint'), cell: ({ row }) => row.original.id },
			count('lines', t('hubUpload.colLines'), (p) => p.lines),
			// A file without Boxes (a per-piece batch) has no Boxes column.
			...(boxes ? [count('boxes', t('hubUpload.colBoxes'), (p) => p.boxes)] : []),
			count('new', t('hubUpload.colNew'), (p) => p.newParcels),
			// Parcels moved in from another batch are rare: the column shows only when there are some.
			...(movedIn ? [count('moved', t('hubUpload.colMoved'), (p) => p.movedIn)] : []),
			{
				id: 'amount',
				header: t('hubUpload.colAmount'),
				meta: { headClassName: 'text-right', cellClassName: 'text-right tabular-nums' },
				cell: ({ row }) => amount(row.original.amountTotal),
			},
		]
	}, [t, movedIn, boxes])
	const data = plan.points as PlanPoint[]
	const { table } = useDataTable({
		data,
		columns,
		rowCount: data.length,
		getRowId: (p) => p.pointId,
		...WHOLE,
	})
	return (
		<DataTable
			table={table}
			withPagination={false}
			maxHeightClassName='max-h-none'
			emptyText={t('hubUpload.noPoints')}
			row={{ attributes: (p) => ({ 'data-testid': `hub-upload-point-${p.pointCode}` }) }}
		/>
	)
}

type RefusedRow = HubPlan['refused'][number]

function RefusedTable({ plan }: { plan: HubPlan }) {
	const { t } = useTranslate()
	const columns = useMemo<ColumnDef<RefusedRow>[]>(
		() => [
			{
				id: 'row',
				header: t('hubUpload.colRow'),
				meta: { headClassName: 'w-24 text-right', cellClassName: 'w-24 text-right tabular-nums' },
				cell: ({ row }) => row.original.row,
			},
			{
				id: 'code',
				header: t('hubUpload.colTracking'),
				cell: ({ row }) => <span className='font-mono'>{row.original.trackingCode || '—'}</span>,
			},
			{
				id: 'point',
				header: t('hubUpload.colCode'),
				cell: ({ row }) => <span className='font-mono'>{row.original.pointCode || '—'}</span>,
			},
			{
				id: 'reason',
				header: t('hubUpload.colReason'),
				cell: ({ row }) => (
					<span className='text-destructive'>
						{t(`hubUpload.reason_${row.original.reason}` as never) || row.original.reason}
					</span>
				),
			},
		],
		[t],
	)
	const data = plan.refused as RefusedRow[]
	const { table } = useDataTable({
		data,
		columns,
		rowCount: data.length,
		getRowId: (r) => `${r.row}-${r.trackingCode}`,
		...WHOLE,
	})
	return (
		<DataTable
			table={table}
			withPagination={false}
			emptyText={t('hubUpload.refusedEmpty')}
			row={{
				attributes: (r) => ({ 'data-testid': 'hub-upload-refused-item', 'data-reason': r.reason }),
			}}
		/>
	)
}

type ChangeKind = 'readdress' | 'cancel' | 'update' | 'conflict'
interface ChangeRow {
	key: string
	kind: ChangeKind
	code: string
	detail: string
}

function ChangesTable({ plan }: { plan: HubPlan }) {
	const { t } = useTranslate()
	// Rows and columns are memoised: the table resets its page whenever its data is a
	// new array, and a reset re-renders — a fresh array on every render never settles.
	const rows = useMemo<ChangeRow[]>(() => {
		const label = (key: string, fallback: string) => t(key as never) || fallback
		const som = (value: number) => t('hubUpload.som', { amount: amount(value) })
		return [
			...plan.readdresses.map((r) => ({
				key: `readdress-${r.trackingCode}`,
				kind: 'readdress' as const,
				code: r.trackingCode,
				detail: `${r.from || '—'} → ${r.to || '—'}`,
			})),
			...plan.cancels.map((c) => ({
				key: `cancel-${c.trackingCode}`,
				kind: 'cancel' as const,
				code: c.trackingCode,
				detail: c.from || '—',
			})),
			...plan.updates.map((u) => ({
				key: `update-${u.trackingCode}`,
				kind: 'update' as const,
				code: u.trackingCode,
				detail: `${amount(u.amountFrom)} → ${som(u.amountTo)}`,
			})),
			...plan.conflicts.map((c) => ({
				key: `conflict-${c.kind}-${c.trackingCode}`,
				kind: 'conflict' as const,
				code: c.trackingCode,
				detail: t(`hubUpload.conflict_${c.kind}`, {
					state: label(`parcelState.${c.state}`, c.state),
					at: c.at || '—',
					from: c.amountFrom === null ? '' : som(c.amountFrom),
					to: c.amountTo === null ? '' : som(c.amountTo),
				}),
			})),
		]
	}, [plan, t])
	const columns = useMemo<ColumnDef<ChangeRow>[]>(
		() => [
			{
				id: 'code',
				header: t('hubUpload.colTracking'),
				cell: ({ row }) => <span className='font-mono'>{row.original.code}</span>,
			},
			{
				id: 'kind',
				header: t('hubUpload.colChange'),
				cell: ({ row }) => {
					const kind = row.original.kind
					const warn = kind === 'cancel' || kind === 'conflict'
					return (
						<Badge
							variant='outline'
							className={cn(warn && 'border-amber-500/60 text-amber-700 dark:text-amber-400')}
						>
							{t(`hubUpload.change_${kind}`)}
						</Badge>
					)
				},
			},
			{ id: 'detail', header: t('hubUpload.colDetail'), cell: ({ row }) => row.original.detail },
		],
		[t],
	)
	const { table } = useDataTable({
		data: rows,
		columns,
		rowCount: rows.length,
		getRowId: (r) => r.key,
		...WHOLE,
	})
	return (
		<DataTable
			table={table}
			withPagination={false}
			emptyText={t('hubUpload.changesEmpty')}
			row={{ attributes: (r) => ({ 'data-testid': `hub-upload-change-${r.kind}` }) }}
		/>
	)
}

/** What the hub answered: the Manifest sent, its revision, and what was done line by line. */
function AppliedLine({ applied }: { applied: HubApplied }) {
	const { t } = useTranslate()
	const tone = applied.refusals.length > 0 ? 'attention' : 'ready'
	return (
		<div
			role='status'
			className={cn('flex flex-col gap-1 rounded-md border px-4 py-2.5', TONE_CLASS[tone])}
			data-testid='hub-upload-applied'
			data-tone={tone}
		>
			<div className='flex flex-wrap items-baseline gap-x-2 font-semibold'>
				<span>{t('hubUpload.appliedTitle')}</span>
				<span className='font-mono'>{applied.dispatchRef}</span>
				<span
					className='text-sm font-normal'
					data-testid='hub-upload-outcome'
					data-outcome={applied.outcome}
				>
					{t(`hubUpload.outcome_${applied.outcome}`, { revision: String(applied.revision) })}
				</span>
			</div>
			<div className='flex flex-wrap gap-x-5 text-sm tabular-nums'>
				<span>
					{t('hubUpload.accepted')} {applied.accepted}
				</span>
				<span>
					{t('hubUpload.refusedCount')} {applied.refused}
				</span>
				{/* What a new revision did — only what happened. */}
				{(
					[
						['readdressed', applied.readdressed],
						['cancelled', applied.cancelled],
						['updated', applied.updated],
					] as const
				)
					.filter(([, n]) => n > 0)
					.map(([key, n]) => (
						<span key={key}>
							{t(`hubUpload.${key}`)} {n}
						</span>
					))}
			</div>
			{applied.refusals.length > 0 && (
				<ul className='flex flex-col gap-0.5 text-sm' data-testid='hub-upload-refusals'>
					{applied.refusals.map((r) => (
						<li key={`${r.command}-${r.trackingCode}`}>
							<span className='font-mono'>{r.trackingCode}</span> ·{' '}
							{t(`hubUpload.command_${r.command}`)} · {r.outcome}
						</li>
					))}
				</ul>
			)}
		</div>
	)
}
